import { create } from 'zustand';
import type { Asset, AssetGroup, GroupType } from '@scio3/shared-types';
import type { ThumbnailTarget } from '../utils/thumbnail-upload';

export type AssetDialogType =
  | 'createFolder'
  | 'rename'
  | 'editTags'
  | 'moveFolder'
  | 'remove'
  | 'getInfo'
  | 'pushToScreens'
  | 'addToPlaylist'
  | 'liveExpire'
  | 'replaceAsset'
  | 'replaceThumbnail'
  | 'editDescription'
  | 'preview'
  | 'playlistUsage'
  | 'confirmCreateAsset'
  | 'folderPermission'
  | 'duplicate'
  | 'feeds'
  | 'feedEdit'
  | 'assetDetail'
  | 'pageDurations'
  | null;

interface AssetDialogContext {
  asset?: Asset;
  assetIds?: string[];
  /** Pre-resolved display name for the moved item (Move dialog title) — assets
   *  have no single name field (originalFileName || appType || webType). */
  itemName?: string;
  folder?: AssetGroup;
  folderIds?: string[];
  /** Selected folder OBJECTS (bulk Edit Tags) — the operation-mode dialog needs
   *  each folder's path+name to build the affected-asset count query. */
  folders?: AssetGroup[];
  /** Team ID of selected folders (for bulk folder moves - filters target to same team) */
  selectedFoldersTeamId?: string;
  /** Folder group type for bulk delete (e.g. 'engage') when folder object is not available */
  folderType?: GroupType;
  /**
   * Which PAGE the dialog is acting for. Engage items are assets in the database, so a deletion
   * has to say `ENGAGE` or its Trash row is stamped `asset` and appears on the Files page. The
   * dialog forwards this whole context to `performTrash`, so it must travel with it.
   */
  objectType?: 'ASSET' | 'ENGAGE';
  /** When true, shows playlist prev/next navigation arrows in preview dialog */
  isPlaylist?: boolean;
  /** Override folder path (used when opened from asset picker in playlist page) */
  currentPath?: string;
  /** Force the asset detail view read-only — e.g. drive-app children (Google Drive /
   *  OneDrive / Dropbox) opened from a playlist cannot be renamed/tagged/deleted. */
  readOnly?: boolean;
  /** Replace Thumbnail: the asset or folder whose thumbnail is replaced. */
  thumbnailTarget?: ThumbnailTarget;
}

interface AssetDialogState {
  activeDialog: AssetDialogType;
  dialogContext: AssetDialogContext;
  openDialog: (dialog: AssetDialogType, context?: AssetDialogContext) => void;
  closeDialog: () => void;
}

export const useAssetDialogStore = create<AssetDialogState>((set) => ({
  activeDialog: null,
  dialogContext: {},
  openDialog: (activeDialog, dialogContext = {}) => set({ activeDialog, dialogContext }),
  closeDialog: () => set({ activeDialog: null, dialogContext: {} }),
}));
